/**
 * @file Maps a project's investments to the beneficiary table rows, one per investment tier.
 */
import { useMemo } from "react";
import { useOutletContext } from "react-router-dom";

import { getFormattedDecimalValue } from "@components/Amount";
import { computeInvestmentName } from "@components/Investment";

import type { BeneficiaryItem, Investment, OutletContext } from "../../types";

/**
 * Builds the sorted list of beneficiary table rows, one per investor and tier combination.
 */
const useItems = (investments: Investment[] = []): BeneficiaryItem[] => {
  const { computeAccountFullname, project } = useOutletContext<OutletContext>();
  const { tiers = [] } = project;

  return useMemo(() => {
    const items: BeneficiaryItem[] = [];

    investments.forEach(investment => {
      const investor = computeInvestmentName(computeAccountFullname, investment);
      const email = investment?.investor?.email || "-";
      const tierAmountsMap = (investment.investments || [])
        .reduce<Record<string, number>>((accumulator, { projectTierId, amount = 0 }) => {
          if (!projectTierId) {
            return accumulator;
          }

          accumulator[projectTierId] = getFormattedDecimalValue(
            accumulator[projectTierId] || 0,
            amount,
            "plus",
          );

          return accumulator;
        }, {});

      tiers.forEach(({ name: tierId, title }, index) => {
        const investedAmount = tierAmountsMap[tierId];

        if (!investedAmount) {
          return;
        }

        items.push({
          key: `${investment.id || investor}-${tierId}-${index}`,
          projectId: investment.projectId,
          investmentId: investment.id,
          investmentName: investment.name,
          investorAccountId: investment.investorAccountId,
          investor,
          email,
          investedAmount,
          tierId,
          className: title || tierId,
          returnRate: tiers[index]?.returnPercentage || 0,
        });
      });
    });

    return items.sort((a, b) =>
      a.investor.localeCompare(b.investor),
    );
  }, [ computeAccountFullname, investments, tiers ]);
};

export default useItems;
